// Pure version-comparison + update-decision core for the remote update-control client.
//
// The build stamps __APP_VERSION__ as CalVer YY.MM.BBBB, optionally "+<sha>"
// (see qa/2026-08-21-secops-calver-versioning-lock.md). app_release_policy (channel 'web')
// supplies latest_version, min_supported_version, force_update, message. Compare treats the
// three numeric parts as major/minor/patch (BBBB leading zeros are fine). This module decides,
// with NO I/O, whether the running client is OK, should show a soft "update available" banner,
// or must be HARD-blocked. Kept pure so it is exhaustively unit-testable.

export interface Semver {
  major: number;
  minor: number;
  patch: number;
  prerelease: string | null;
}

/** Split the build stamp into its marketing core and optional build-metadata sha ("26.08.0001+abc"). */
export function parseAppVersion(raw: string): { version: string; sha: string | null } {
  const trimmed = (raw ?? "").trim();
  const plus = trimmed.indexOf("+");
  if (plus === -1) {
    return { version: trimmed, sha: null };
  }
  const sha = trimmed.slice(plus + 1);
  return { version: trimmed.slice(0, plus), sha: sha.length > 0 ? sha : null };
}

/**
 * Parse "major.minor.patch[-prerelease]" (build metadata ignored). Returns null if unparseable.
 * CalVer YY.MM.BBBB uses the same three-part shape (year / month / build-in-month).
 */
export function parseSemver(raw: string): Semver | null {
  if (typeof raw !== "string") {
    return null;
  }
  const core = raw.trim().split("+")[0] ?? "";
  const match = /^(\d+)\.(\d+)\.(\d+)(?:-([0-9A-Za-z.-]+))?$/.exec(core);
  if (match === null) {
    return null;
  }
  return {
    major: Number(match[1]),
    minor: Number(match[2]),
    patch: Number(match[3]),
    prerelease: match[4] ?? null,
  };
}

/**
 * Compare two versions by semver precedence (core numeric; a release outranks a prerelease of the
 * same core; two prereleases compared lexically — a deliberate simplification). Returns -1/0/1, or
 * null if EITHER version is unparseable (callers must treat null as "cannot compare").
 */
export function compareSemver(a: string, b: string): number | null {
  const pa = parseSemver(a);
  const pb = parseSemver(b);
  if (pa === null || pb === null) {
    return null;
  }
  if (pa.major !== pb.major) return pa.major < pb.major ? -1 : 1;
  if (pa.minor !== pb.minor) return pa.minor < pb.minor ? -1 : 1;
  if (pa.patch !== pb.patch) return pa.patch < pb.patch ? -1 : 1;
  if (pa.prerelease === null && pb.prerelease === null) return 0;
  if (pa.prerelease === null) return 1; // release > prerelease
  if (pb.prerelease === null) return -1; // prerelease < release
  if (pa.prerelease === pb.prerelease) return 0;
  return pa.prerelease < pb.prerelease ? -1 : 1;
}

export type UpdateLevel = "ok" | "soft" | "hard";

export interface UpdateState {
  level: UpdateLevel;
  message: string | null;
  latestVersion: string | null;
}

export interface UpdatePolicyInput {
  latestVersion: string;
  minSupportedVersion: string;
  forceUpdate: boolean;
  message: string | null;
}

/**
 * Decide the update state for `currentVersion` against the policy:
 *   - HARD block when force_update is set, OR current < min_supported_version.
 *   - SOFT banner when (not hard) current < latest_version.
 *   - OK otherwise.
 * If `currentVersion` is unparseable we cannot compare, so only an EXPLICIT force_update hard-blocks
 * (we never brick the app on our own malformed version string).
 */
export function evaluateUpdateState(input: {
  currentVersion: string;
  policy: UpdatePolicyInput;
}): UpdateState {
  const { currentVersion, policy } = input;

  let hard = policy.forceUpdate === true;
  if (!hard && policy.minSupportedVersion.length > 0) {
    const cmp = compareSemver(currentVersion, policy.minSupportedVersion);
    if (cmp !== null && cmp < 0) {
      hard = true;
    }
  }

  let soft = false;
  if (!hard && policy.latestVersion.length > 0) {
    const cmp = compareSemver(currentVersion, policy.latestVersion);
    if (cmp !== null && cmp < 0) {
      soft = true;
    }
  }

  const level: UpdateLevel = hard ? "hard" : soft ? "soft" : "ok";
  return {
    level,
    message: policy.message ?? null,
    latestVersion: policy.latestVersion.length > 0 ? policy.latestVersion : null,
  };
}
